import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Truck, MapPin, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShippingCalculator = ({ cartItems, subtotal, onShippingSelected }) => {
  const [country, setCountry] = useState('US');
  const [zipCode, setZipCode] = useState('');
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [loading, setLoading] = useState(false);
  const [freeShippingInfo, setFreeShippingInfo] = useState(null);

  const countries = [
    // North America
    { code: 'US', name: 'United States', region: 'North America' },
    { code: 'CA', name: 'Canada', region: 'North America' },
    { code: 'MX', name: 'Mexico', region: 'North America' },
    
    // Europe (40+ countries)
    { code: 'AL', name: 'Albania', region: 'Europe' },
    { code: 'AD', name: 'Andorra', region: 'Europe' },
    { code: 'AT', name: 'Austria', region: 'Europe' },
    { code: 'BY', name: 'Belarus', region: 'Europe' },
    { code: 'BE', name: 'Belgium', region: 'Europe' },
    { code: 'BA', name: 'Bosnia and Herzegovina', region: 'Europe' },
    { code: 'BG', name: 'Bulgaria', region: 'Europe' },
    { code: 'HR', name: 'Croatia', region: 'Europe' },
    { code: 'CY', name: 'Cyprus', region: 'Europe' },
    { code: 'CZ', name: 'Czech Republic', region: 'Europe' },
    { code: 'DK', name: 'Denmark', region: 'Europe' },
    { code: 'EE', name: 'Estonia', region: 'Europe' },
    { code: 'FI', name: 'Finland', region: 'Europe' },
    { code: 'FR', name: 'France', region: 'Europe' },
    { code: 'DE', name: 'Germany', region: 'Europe' },
    { code: 'GR', name: 'Greece', region: 'Europe' },
    { code: 'HU', name: 'Hungary', region: 'Europe' },
    { code: 'IS', name: 'Iceland', region: 'Europe' },
    { code: 'IE', name: 'Ireland', region: 'Europe' },
    { code: 'IT', name: 'Italy', region: 'Europe' },
    { code: 'XK', name: 'Kosovo', region: 'Europe' },
    { code: 'LV', name: 'Latvia', region: 'Europe' },
    { code: 'LI', name: 'Liechtenstein', region: 'Europe' },
    { code: 'LT', name: 'Lithuania', region: 'Europe' },
    { code: 'LU', name: 'Luxembourg', region: 'Europe' },
    { code: 'MT', name: 'Malta', region: 'Europe' },
    { code: 'MD', name: 'Moldova', region: 'Europe' },
    { code: 'MC', name: 'Monaco', region: 'Europe' },
    { code: 'ME', name: 'Montenegro', region: 'Europe' },
    { code: 'NL', name: 'Netherlands', region: 'Europe' },
    { code: 'MK', name: 'North Macedonia', region: 'Europe' },
    { code: 'NO', name: 'Norway', region: 'Europe' },
    { code: 'PL', name: 'Poland', region: 'Europe' },
    { code: 'PT', name: 'Portugal', region: 'Europe' },
    { code: 'RO', name: 'Romania', region: 'Europe' },
    { code: 'RU', name: 'Russia', region: 'Europe' },
    { code: 'SM', name: 'San Marino', region: 'Europe' },
    { code: 'RS', name: 'Serbia', region: 'Europe' },
    { code: 'SK', name: 'Slovakia', region: 'Europe' },
    { code: 'SI', name: 'Slovenia', region: 'Europe' },
    { code: 'ES', name: 'Spain', region: 'Europe' },
    { code: 'SE', name: 'Sweden', region: 'Europe' },
    { code: 'CH', name: 'Switzerland', region: 'Europe' },
    { code: 'UA', name: 'Ukraine', region: 'Europe' },
    { code: 'GB', name: 'United Kingdom', region: 'Europe' },
    { code: 'VA', name: 'Vatican City', region: 'Europe' },
    
    // South America (13 countries)
    { code: 'AR', name: 'Argentina', region: 'South America' },
    { code: 'BO', name: 'Bolivia', region: 'South America' },
    { code: 'BR', name: 'Brazil', region: 'South America' },
    { code: 'CL', name: 'Chile', region: 'South America' },
    { code: 'CO', name: 'Colombia', region: 'South America' },
    { code: 'EC', name: 'Ecuador', region: 'South America' },
    { code: 'FK', name: 'Falkland Islands', region: 'South America' },
    { code: 'GF', name: 'French Guiana', region: 'South America' },
    { code: 'GY', name: 'Guyana', region: 'South America' },
    { code: 'PY', name: 'Paraguay', region: 'South America' },
    { code: 'PE', name: 'Peru', region: 'South America' },
    { code: 'SR', name: 'Suriname', region: 'South America' },
    { code: 'UY', name: 'Uruguay', region: 'South America' },
    { code: 'VE', name: 'Venezuela', region: 'South America' },
    
    // Pacific
    { code: 'AU', name: 'Australia', region: 'Pacific' },
    { code: 'NZ', name: 'New Zealand', region: 'Pacific' },
  ];

  useEffect(() => {
    if (cartItems.length > 0) {
      calculateShipping();
    }
  }, [country, cartItems, subtotal]);

  const calculateShipping = async () => {
    if (cartItems.length === 0) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/shipping/calculate`, {
        country: country,
        zip_code: zipCode,
        items: cartItems.map(item => ({
          product_id: item.id,
          quantity: item.quantity
        })),
        subtotal: subtotal
      });

      setShippingOptions(response.data.options);
      setFreeShippingInfo({
        eligible: response.data.free_shipping_eligible,
        threshold: response.data.free_shipping_threshold,
        amount_until_free: response.data.amount_until_free
      });

      // Auto-select first option
      if (response.data.options.length > 0) {
        const firstOption = response.data.options[0];
        setSelectedShipping(firstOption);
        onShippingSelected(firstOption);
      }
    } catch (error) {
      console.error('Error calculating shipping:', error);
      toast.error('Failed to calculate shipping rates');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectShipping = (option) => {
    setSelectedShipping(option);
    onShippingSelected(option);
  };

  if (cartItems.length === 0) return null;

  return (
    <Card className="bg-gray-900 border-gray-800">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Truck className="w-5 h-5 text-blue-500" />
          <h3 className="text-lg font-bold text-white">Shipping Options</h3>
        </div>

        {/* Free Shipping Banner */}
        {freeShippingInfo && !freeShippingInfo.eligible && freeShippingInfo.amount_until_free > 0 && (
          <div className="mb-4 p-3 bg-blue-900/30 border border-blue-500/50 rounded-lg">
            <p className="text-sm text-blue-300">
              💰 Add <span className="font-bold">${freeShippingInfo.amount_until_free.toFixed(2)}</span> more to get <span className="font-bold">FREE SHIPPING</span>!
            </p>
          </div>
        )}

        {freeShippingInfo && freeShippingInfo.eligible && (
          <div className="mb-4 p-3 bg-green-900/30 border border-green-500/50 rounded-lg">
            <p className="text-sm text-green-300 flex items-center gap-2">
              <Check className="w-4 h-4" />
              <span className="font-bold">FREE SHIPPING</span> applied to your order!
            </p>
          </div>
        )}

        {/* Country Selector */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <MapPin className="w-4 h-4 inline mr-1" />
            Ship to:
          </label>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 outline-none"
          >
            {/* Group by region */}
            <optgroup label="North America">
              {countries.filter(c => c.region === 'North America').map(c => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </optgroup>
            <optgroup label="Europe">
              {countries.filter(c => c.region === 'Europe').map(c => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </optgroup>
            <optgroup label="South America">
              {countries.filter(c => c.region === 'South America').map(c => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </optgroup>
            <optgroup label="Pacific">
              {countries.filter(c => c.region === 'Pacific').map(c => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </optgroup>
          </select>
        </div>

        {/* Shipping Options */}
        {loading ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="text-gray-400 text-sm mt-2">Calculating rates...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {shippingOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => handleSelectShipping(option)}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  selectedShipping?.method === option.method && selectedShipping?.carrier === option.carrier
                    ? 'border-blue-500 bg-blue-900/30'
                    : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-white">{option.description}</p>
                      {selectedShipping?.method === option.method && selectedShipping?.carrier === option.carrier && (
                        <Check className="w-4 h-4 text-blue-500" />
                      )}
                    </div>
                    <p className="text-sm text-gray-400 mt-1">{option.estimated_days}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-white">
                      {option.price === 0 ? 'FREE' : `$${option.price.toFixed(2)}`}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {shippingOptions.length === 0 && !loading && (
          <p className="text-gray-400 text-sm text-center py-4">
            Select a country to see shipping options
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default ShippingCalculator;
